import {TableColumnData, TableHeaderCell, TableHeaderRow} from "./TableColumnData";
import {Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import React, {ReactNode, SyntheticEvent, useCallback, useMemo} from "react";
import {SetState} from "../../util/FormHandlers";


function DataTableRow<R>({row, isSelected, toggleSelection, children}: {
    row: R,
    isSelected: boolean | undefined,
    toggleSelection?: (row: R) => void,
    children: ReactNode,
}) {

    const handleSelectRow = useCallback(
        (e: SyntheticEvent) => {
            if (toggleSelection) {
                e.stopPropagation();
                e.preventDefault();
                toggleSelection(row);
            }
        },
        [row, toggleSelection],
    )

    return (
        <TableRow
            hover
            selected={isSelected}
            onClick={handleSelectRow}
            sx={{cursor: isSelected !== undefined ? "pointer" : undefined}}
        >
            {isSelected !== undefined && (
                <TableCell padding="checkbox">
                    <Checkbox
                        checked={isSelected}
                        onChange={handleSelectRow}
                    />
                </TableCell>
            )}
            {children}
        </TableRow>
    );
}

export function DataTable<R>({columns, rows, id, selectedRows, setSelectedRows}: {
    columns: TableColumnData<R>[],
    rows: ReadonlyArray<R>,
    id: (row: R) => any,
    selectedRows?: Set<R>,
    setSelectedRows?: SetState<Set<R>>,
}) {

    const allSelected = useMemo(() => selectedRows && (selectedRows.size === 0 ? (
        'none'
    ) : selectedRows.size === rows.length ? (
        'all'
    ) : (
        'indeterminate'
    )), [rows, selectedRows]);

    const selectAll = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedRows && setSelectedRows(event.target.checked ? new Set(rows) : new Set());
    }, [setSelectedRows, rows]);

    const toggleSelection = useCallback((row: R) => {
        setSelectedRows && setSelectedRows(prevSelectedRows => {
                const nextSelectedRows = new Set(prevSelectedRows);
                if (prevSelectedRows.has(row)) {
                    nextSelectedRows.delete(row);
                } else {
                    nextSelectedRows.add(row);
                }
                return nextSelectedRows;
            }
        )
    }, [setSelectedRows]);

    return (
        <TableContainer component={Paper} sx={{mt: 3}}>
            <Table>
                <TableHead>
                    <TableHeaderRow>
                        {allSelected && (
                            <TableCell padding="checkbox" width="4em">
                                <Checkbox
                                    indeterminate={allSelected === 'indeterminate'}
                                    checked={allSelected === 'all'}
                                    onChange={selectAll}
                                />
                            </TableCell>
                        )}
                        {columns.map(column => (
                            <TableHeaderCell key={column.key} column={column}/>
                        ))}
                    </TableHeaderRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <DataTableRow
                            key={id(row)}
                            row={row}
                            isSelected={selectedRows && selectedRows.has(row)}
                            toggleSelection={setSelectedRows ? toggleSelection : undefined}
                        >
                            {columns.map(column => (
                                <React.Fragment key={column.key}>{column.cell(row, rowIndex)}</React.Fragment>
                            ))}
                        </DataTableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
